import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTrackUsage } from '../../hooks/useTrackUsage';
import { publicViewService } from '../../services/TaskViewService';
import TaskCalendar from './TaskCalendar';
import { monthGrid, monthKey } from './calendarUi';
import { dateLabel } from './taskFilters';

const GONE = 'This link is no longer available. Ask the person who shared it for a new link.';

const todayIso = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// The calendar's row shape from a link's row, which carries only what a link may show
const asRow = (r, i) => ({
    id: `p${i}`,
    title: r.title,
    languageLabel: r.language,
    stageName: r.stage,
    date: r.postingDate,
    status: r.done ? 'COMPLETED' : 'OPEN',
    category: r.category,
});
const asTray = (t) => (t ? { count: t.count, rows: t.rows.map(asRow) } : null);

/**
 * /v/:token: a saved filter's calendar or list, shared without login (rev6 Part 4, D35). Read
 * only, placed by posting date. The visitor moves between months and switches Month and List;
 * the filters are fixed and nothing opens.
 */
const PublicViewPage = () => {
    useTrackUsage('PublicViewPage', 'screen', 'tasks');
    const { token } = useParams();
    const service = useMemo(() => publicViewService(token), [token]);
    const [month, setMonth] = useState(() => monthKey(todayIso()));
    const [mode, setMode] = useState('month');
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let live = true;
        const weeks = monthGrid(month);
        setLoading(true);
        service
            .view(weeks[0][0], weeks[weeks.length - 1][6])
            .then((d) => live && (setData(d), setError(null)))
            .catch((e) => {
                if (!live) return;
                const status = e?.response?.status;
                setError(
                    status === 404 || !status ? GONE : e?.response?.data?.message || 'Something went wrong. Try again.',
                );
            })
            .finally(() => live && setLoading(false));
        return () => {
            live = false;
        };
    }, [service, month]);

    useEffect(() => {
        document.title = data?.name ? `${data.name} · Shared via AmrutVaani` : 'Shared via AmrutVaani';
    }, [data?.name]);

    const calendarData = useMemo(
        () =>
            data && {
                ...data,
                rows: data.rows.map(asRow),
                overdue: asTray(data.overdue),
                noDate: asTray(data.noDate),
            },
        [data],
    );

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
            <Box component="header" sx={{ backgroundColor: '#2C3D50', color: '#fff', py: 1.5 }}>
                <Box
                    sx={{
                        maxWidth: 1280,
                        mx: 'auto',
                        px: { xs: 2, sm: 3 },
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                    }}
                >
                    <img src="/SRMD-Logo.png" alt="SRMD" style={{ height: 36 }} />
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        Shared via AmrutVaani
                    </Typography>
                </Box>
            </Box>
            <Box component="main" sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>
                {error && !data && <Alert severity="info">{error}</Alert>}
                {!error && !data && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress />
                    </Box>
                )}
                {calendarData && (
                    <>
                        <Typography variant="h5" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {data.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Read only. Tasks are placed on their posting date.
                        </Typography>
                        {error && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}
                        <Box sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.15s' }}>
                            <TaskCalendar
                                data={calendarData}
                                month={month}
                                onMonthChange={setMonth}
                                mode={mode}
                                onModeChange={setMode}
                                group
                                // A link carries no ids: the same title on one day is one file
                                groupKey={(r) => r.title}
                                isPublic
                                dateFilterLabel={data.dateFilter ? dateLabel(data.dateFilter) : null}
                            />
                        </Box>
                    </>
                )}
            </Box>
        </Box>
    );
};

export default PublicViewPage;
